import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import {
  Users,
  MessageSquare,
  Zap,
  DollarSign,
  Clock,
  TrendingUp,
  Activity,
  BarChart3
} from 'lucide-react'

// Simple Line Chart Component using SVG
function LineChart({ data, dataKey, color }: { data: any[], dataKey: string, color: string }) {
  if (!data || data.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data available</div>
  }

  const width = 800
  const height = 200
  const padding = { top: 20, right: 20, bottom: 40, left: 50 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  // Extract values and find min/max for scaling
  const values = data.map(d => d[dataKey] || 0)
  const maxValue = Math.max(...values, 1) // At least 1 to avoid division by zero
  const minValue = Math.min(...values, 0)

  // Calculate points
  const points = data.map((d, index) => {
    const x = padding.left + (index / (data.length - 1)) * chartWidth
    const value = d[dataKey] || 0
    const y = padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight
    return { x, y, value, date: d.week }
  })

  // Create path for line
  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="w-full" viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + chartHeight - (ratio * chartHeight)
          const value = minValue + (maxValue - minValue) * ratio
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="10"
                fill="#6b7280"
              >
                {Math.round(value)}
              </text>
            </g>
          )
        })}

        {/* Line */}
        <path
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={color}
              className="hover:r-6 transition-all"
            />
            {/* Tooltip on hover */}
            <title>{`${formatDate(point.date)}: ${point.value}`}</title>
          </g>
        ))}

        {/* X-axis labels */}
        {points.map((point, index) => {
          if (index % 2 === 0 || index === points.length - 1) {
            return (
              <text
                key={index}
                x={point.x}
                y={height - padding.bottom + 15}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {formatDate(point.date)}
              </text>
            )
          }
          return null
        })}
      </svg>
    </div>
  )
}

interface AnalyticsData {
  total_users: number
  active_users_today: number
  active_users_week: number
  active_users_month: number
  total_conversations: number
  total_messages: number
  total_tokens_used: number
  estimated_total_cost: number
  average_response_time_ms: number
  peak_usage_hour: number
  most_popular_features: Array<{feature: string, usage_count: number}>
  avg_messages_per_conversation?: number
  avg_conversations_per_user?: number
  growth_trends?: {
    conversations_wow_growth: number
    messages_wow_growth: number
    users_wow_growth: number
    conversations_mom_growth: number
    messages_mom_growth: number
    users_mom_growth: number
    history?: Array<{
      week: string
      conversations: number
      messages: number
      active_users: number
    }>
  }
  cost_trends: any[]
  usage_trends: any[]
}

interface UsageTrend {
  date: string
  conversations: number
  messages: number
  tokens: number
  cost: number
  active_users: number
}

export function AnalyticsDashboard() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [usageTrends, setUsageTrends] = useState<UsageTrend[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    console.log('AnalyticsDashboard mounted')
    loadAnalyticsData()
  }, [])

  const loadAnalyticsData = async () => {
    try {
      setIsLoading(true)
      console.log('Loading analytics data...')

      // Test if we can access other admin endpoints first
      try {
        console.log('Testing admin access with getAdminStats...')
        const adminStats = await apiClient.getAdminStats()
        console.log('Admin stats successful:', adminStats)
      } catch (adminError: any) {
        console.error('Admin stats failed:', adminError)
      }

      // Now try analytics endpoints
      const [overview, trends] = await Promise.all([
        apiClient.getAnalyticsOverview(),
        apiClient.getUsageTrends('daily', 30)
      ])

      console.log('Analytics overview response:', overview)
      console.log('Usage trends response:', trends)

      setAnalyticsData(overview)
      // Backend returns { period, data } (UsageTrendsResponse). Keep backward compatibility if shape changes.
      setUsageTrends(trends.data || trends.trends || [])
    } catch (error: any) {
      console.error('Failed to load analytics data:', error)
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      })

      toast({
        title: "Error",
        description: `Failed to load analytics data: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`
  }

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading analytics...</div>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-500">No analytics data available</div>
      </div>
    )
  }

  const recentTrend = usageTrends.slice(-7) // Last 7 days
  const totalMessagesLast7Days = recentTrend.reduce((sum, day) => sum + day.messages, 0)
  const totalConversationsLast7Days = recentTrend.reduce((sum, day) => sum + day.conversations, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(analyticsData.total_users)}</div>
            <p className="text-xs text-gray-500 mt-1">
              {analyticsData.active_users_month} active this month
            </p>
          </CardContent>
        </Card>

        {/* Total Conversations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(analyticsData.total_conversations)}</div>
            <p className="text-xs text-gray-500 mt-1">
              {totalConversationsLast7Days} in last 7 days
            </p>
          </CardContent>
        </Card>

        {/* Total Messages */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Messages</CardTitle>
            <Activity className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(analyticsData.total_messages)}</div>
            <p className="text-xs text-gray-500 mt-1">
              {totalMessagesLast7Days} in last 7 days
            </p>
          </CardContent>
        </Card>

        {/* Tokens Used */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Tokens Used</CardTitle>
            <Zap className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(analyticsData.total_tokens_used)}</div>
            <p className="text-xs text-gray-500 mt-1">
              Total tokens processed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Estimated Cost */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Estimated Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analyticsData.estimated_total_cost)}</div>
            <p className="text-xs text-gray-500 mt-1">
              Total estimated spend
            </p>
          </CardContent>
        </Card>

        {/* Average Response Time */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsData.average_response_time_ms ? formatTime(analyticsData.average_response_time_ms) : 'N/A'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Average processing time
            </p>
          </CardContent>
        </Card>

        {/* Peak Usage Hour */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Peak Usage Hour</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsData.peak_usage_hour}:00
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Most active hour
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Usage Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Recent Activity (Last 7 Days)
            </CardTitle>
            <CardDescription>Daily usage statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentTrend.length > 0 ? (
                recentTrend.map((day, index) => (
                  <div key={day.date} className="flex items-center justify-between py-2 border-b last:border-b-0">
                    <div>
                      <p className="font-medium">
                        {new Date(day.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-xs text-gray-500">{day.active_users} active users</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{day.messages} messages</p>
                      <p className="text-xs text-gray-500">{day.conversations} conversations</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No recent activity data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Popular Features */}
        <Card>
          <CardHeader>
            <CardTitle>Popular Features</CardTitle>
            <CardDescription>Most frequently used features</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analyticsData.most_popular_features.length > 0 ? (
                analyticsData.most_popular_features.slice(0, 10).map((feature, index) => (
                  <div key={feature.feature} className="flex items-center justify-between py-2 border-b last:border-b-0">
                    <div>
                      <p className="font-medium capitalize">{feature.feature.replace('_', ' ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{feature.usage_count}</p>
                      <p className="text-xs text-gray-500">uses</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No feature usage data available yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Trends */}
      {analyticsData.growth_trends && analyticsData.growth_trends.history && analyticsData.growth_trends.history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Growth Trends</CardTitle>
            <CardDescription>8-week trend analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Line Chart for Users */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <p className="text-sm font-medium text-blue-600">Active Users</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>WoW: <span className={`font-bold ${analyticsData.growth_trends.users_wow_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.users_wow_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.users_wow_growth}%
                    </span></span>
                    <span>MoM: <span className={`font-bold ${analyticsData.growth_trends.users_mom_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.users_mom_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.users_mom_growth}%
                    </span></span>
                  </div>
                </div>
                <LineChart data={analyticsData.growth_trends.history} dataKey="active_users" color="#3b82f6" />
              </div>

              {/* Line Chart for Conversations */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-medium text-green-600">Conversations</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>WoW: <span className={`font-bold ${analyticsData.growth_trends.conversations_wow_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.conversations_wow_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.conversations_wow_growth}%
                    </span></span>
                    <span>MoM: <span className={`font-bold ${analyticsData.growth_trends.conversations_mom_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.conversations_mom_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.conversations_mom_growth}%
                    </span></span>
                  </div>
                </div>
                <LineChart data={analyticsData.growth_trends.history} dataKey="conversations" color="#10b981" />
              </div>

              {/* Line Chart for Messages */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-purple-600" />
                    <p className="text-sm font-medium text-purple-600">Messages</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>WoW: <span className={`font-bold ${analyticsData.growth_trends.messages_wow_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.messages_wow_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.messages_wow_growth}%
                    </span></span>
                    <span>MoM: <span className={`font-bold ${analyticsData.growth_trends.messages_mom_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.growth_trends.messages_mom_growth >= 0 ? '+' : ''}{analyticsData.growth_trends.messages_mom_growth}%
                    </span></span>
                  </div>
                </div>
                <LineChart data={analyticsData.growth_trends.history} dataKey="messages" color="#a855f7" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Overview */}
      <Card>
        <CardHeader>
          <CardTitle>System Overview</CardTitle>
          <CardDescription>Key performance indicators</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{analyticsData.active_users_today}</div>
              <div className="text-sm text-blue-600">Active Today</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{analyticsData.active_users_week}</div>
              <div className="text-sm text-green-600">Active This Week</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{analyticsData.active_users_month}</div>
              <div className="text-sm text-purple-600">Active This Month</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {analyticsData.avg_messages_per_conversation?.toFixed(1) || ((analyticsData.total_messages / analyticsData.total_conversations) || 0).toFixed(1)}
              </div>
              <div className="text-sm text-yellow-600">Avg Messages/Chat</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {analyticsData.avg_conversations_per_user?.toFixed(1) || ((analyticsData.total_conversations / analyticsData.total_users) || 0).toFixed(1)}
              </div>
              <div className="text-sm text-orange-600">Avg Chats/User</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}